document.addEventListener('DOMContentLoaded', () => {
  const CONFIG = {
    tierOptionName: 'Tiered Pricing',
    tierTableSelector: '[data-tiered-pricing]',
    tierRowSelector: '[data-tier-threshold]',
    productFormSelector: 'form[action*="/cart/add"]',
    quantityInputSelector: 'input[name="quantity"]',
    cartSections: ['cart-drawer', 'cart-icon-bubble', 'footer-cart-icon']
  };

  const state = {
    pageProduct: null,
    pageTierOptionIndex: -1,
    pageTiers: [],
    busy: false,
    timer: null,
    productCache: new Map()
  };

  init();

  async function init() {
    await initProductPage();

    document.addEventListener('click', handleAddToCart, true);
    document.addEventListener('submit', blockNativeSubmit, true);

    document.addEventListener('input', handleProductPageInput);
    document.addEventListener('change', handleProductPageInput);

    document.addEventListener('click', handleCartChanged, false);
    document.addEventListener('change', handleCartChanged, false);

    window.TieredPricingNormalizer = scheduleNormalise;

    updateTable();
  }

  async function initProductPage() {
    const table = getTierTable();
    if (!table) return;

    const handle = getCurrentProductHandle();
    if (!handle) return;

    const product = await getProductByHandle(handle);
    const tierOptionIndex = getTierOptionIndex(product);

    if (tierOptionIndex === -1) return;

    state.pageProduct = product;
    state.pageTierOptionIndex = tierOptionIndex;
    state.pageTiers = getProductTiers(product, tierOptionIndex);

    getTierRows().forEach(row => {
      row.addEventListener('click', () => {
        const qty = Number(row.dataset.tierThreshold);
        if (Number.isNaN(qty)) return;

        setProductQuantity(qty);
        updateTable();
      });
    });
  }

  function getCurrentProductHandle() {
    return window.location.pathname.split('/products/')[1]?.split(/[?#]/)[0] || null;
  }

  function getTierTable() {
    return document.querySelector(CONFIG.tierTableSelector);
  }

  function getTierRows() {
    const table = getTierTable();
    return table ? [...table.querySelectorAll(CONFIG.tierRowSelector)] : [];
  }

  function getQtyInput() {
    return document.querySelector(CONFIG.quantityInputSelector);
  }

  function getProductQuantity() {
    const qty = Number(getQtyInput()?.value);
    return qty > 0 ? qty : 1;
  }

  function setProductQuantity(qty) {
    const input = getQtyInput();
    if (!input) return;

    input.value = String(qty);
    input.setAttribute('value', String(qty));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  async function getCart() {
    return fetch(`/cart.js?_=${Date.now()}`).then(r => r.json());
  }

  async function getProductByHandle(handle) {
    if (state.productCache.has(handle)) {
      return state.productCache.get(handle);
    }

    const product = await fetch(`/products/${handle}.js`).then(r => r.json());
    state.productCache.set(handle, product);
    return product;
  }

  function getTierOptionIndex(product) {
    return product.options.findIndex(option => option.name === CONFIG.tierOptionName);
  }

  function getProductTiers(product, tierOptionIndex) {
    return product.variants
      .map(variant => ({
        threshold: Number(variant.options[tierOptionIndex]),
        variant
      }))
      .filter(tier => !Number.isNaN(tier.threshold))
      .sort((a, b) => a.threshold - b.threshold);
  }

  function getBestTier(tiers, qty) {
    let best = tiers[0];

    tiers.forEach(tier => {
      if (qty >= tier.threshold) {
        best = tier;
      }
    });

    return best;
  }

  function getPageOptions() {
    const product = state.pageProduct;
    const tierOptionIndex = state.pageTierOptionIndex;

    return product.options.map((option, index) => {
      if (index === tierOptionIndex) return null;

      const escaped = CSS.escape(option.name);
      const radio = document.querySelector(`input[type="radio"][name="${escaped}"]:checked`);
      const select = document.querySelector(`select[name="${escaped}"]`);

      if (radio) return radio.value;
      if (select) return select.value;

      return product.variants[0].options[index];
    });
  }

  function getCartItemOptions(item, product, tierOptionIndex) {
    return product.options.map((option, index) => {
      if (index === tierOptionIndex) return null;

      const found = item.options_with_values?.find(o => o.name === option.name);
      return found ? found.value : product.variants[0].options[index];
    });
  }

  function optionKey(options) {
    return JSON.stringify(options);
  }

  function findVariant(product, tierOptionIndex, tier, options) {
    return product.variants.find(variant => {
      return variant.options.every((value, index) => {
        if (index === tierOptionIndex) return Number(value) === tier.threshold;
        return value === options[index];
      });
    }) || tier.variant;
  }

  async function buildGlobalPlan(cart, addGroup = null) {
    const products = new Map();

    for (const item of cart.items) {
      if (!item.handle) continue;

      const product = await getProductByHandle(item.handle);
      const tierOptionIndex = getTierOptionIndex(product);

      if (tierOptionIndex === -1) continue;

      if (!products.has(product.id)) {
        products.set(product.id, {
          product,
          tierOptionIndex,
          tiers: getProductTiers(product, tierOptionIndex),
          items: [],
          groups: new Map()
        });
      }

      products.get(product.id).items.push(item);
    }

    if (addGroup && state.pageProduct) {
      const product = state.pageProduct;
      const tierOptionIndex = state.pageTierOptionIndex;

      if (!products.has(product.id)) {
        products.set(product.id, {
          product,
          tierOptionIndex,
          tiers: state.pageTiers,
          items: [],
          groups: new Map()
        });
      }

      const productData = products.get(product.id);
      const key = optionKey(addGroup.options);

      productData.groups.set(key, {
        options: addGroup.options,
        quantity: addGroup.quantity
      });
    }

    products.forEach(productData => {
      productData.items.forEach(item => {
        const options = getCartItemOptions(
          item,
          productData.product,
          productData.tierOptionIndex
        );

        const key = optionKey(options);

        if (!productData.groups.has(key)) {
          productData.groups.set(key, {
            options,
            quantity: 0
          });
        }

        productData.groups.get(key).quantity += item.quantity;
      });
    });

    const updates = {};
    const itemsToAddMap = new Map();
    let needsChange = false;

    products.forEach(productData => {
      const totalQty = [...productData.groups.values()]
        .reduce((sum, group) => sum + group.quantity, 0);

      if (totalQty <= 0 || !productData.tiers.length) return;

      const bestTier = getBestTier(productData.tiers, totalQty);
      const desiredMap = new Map();
      const currentMap = new Map();

      productData.groups.forEach(group => {
        if (group.quantity <= 0) return;

        const variant = findVariant(
          productData.product,
          productData.tierOptionIndex,
          bestTier,
          group.options
        );

        const id = Number(variant.id);
        desiredMap.set(id, (desiredMap.get(id) || 0) + group.quantity);
      });

      productData.items.forEach(item => {
        const id = Number(item.id);
        currentMap.set(id, (currentMap.get(id) || 0) + item.quantity);
      });

      let productAlreadyCorrect = currentMap.size === desiredMap.size;

      if (productAlreadyCorrect) {
        desiredMap.forEach((qty, id) => {
          if (currentMap.get(id) !== qty) {
            productAlreadyCorrect = false;
          }
        });
      }

      if (productAlreadyCorrect) return;

      needsChange = true;

      productData.items.forEach(item => {
        updates[item.key] = 0;
      });

      desiredMap.forEach((quantity, id) => {
        itemsToAddMap.set(id, {
          id,
          quantity
        });
      });
    });

    return {
      updates,
      items: [...itemsToAddMap.values()],
      noChange: !needsChange
    };
  }

  async function applyPlan(plan, withSections = false) {
    if (plan.noChange) return null;

    if (Object.keys(plan.updates).length) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: plan.updates })
      });
    }

    if (!plan.items.length) return null;

    const body = {
      items: plan.items
    };

    if (withSections) {
      body.sections = CONFIG.cartSections;
      body.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    return response.json();
  }

  function isTieredMainProductForm(form) {
  const productInfo = form.closest('product-info');
  const table = getTierTable();

  return Boolean(productInfo && table && productInfo.contains(table));
}
  async function handleAddToCart(event) {
    if (!state.pageProduct) return;

    const button = event.target.closest('button[type="submit"], input[type="submit"]');
    if (!button) return;

    const form = button.closest(CONFIG.productFormSelector);
if (!form || !isTieredMainProductForm(form)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (state.busy) return;
    state.busy = true;

    try {
      const cart = await getCart();

      const plan = await buildGlobalPlan(cart, {
        options: getPageOptions(),
        quantity: getProductQuantity()
      });

      const response = await applyPlan(plan, true);

renderCartResponse(response);

setProductQuantity(1);
updateTable();
    } catch (error) {
      console.error('Tiered pricing add failed:', error);
    } finally {
      state.busy = false;
    }
  }

  function blockNativeSubmit(event) {
    if (!state.pageProduct) return;

    const form = event.target.closest(CONFIG.productFormSelector);
if (!form || !isTieredMainProductForm(form)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function handleProductPageInput(event) {
    if (
      event.target.matches(CONFIG.quantityInputSelector) ||
      event.target.matches('input[type="radio"]') ||
      event.target.matches('select')
    ) {
      updateTable();
    }
  }

  function handleCartChanged(event) {
    const drawer = event.target.closest('cart-drawer, #CartDrawer, .cart-drawer, cart-items, #main-cart-items');
    if (!drawer) return;

    const relevant =
      event.target.closest('.quantity__button') ||
      event.target.closest('input[name="updates[]"], .quantity__input') ||
      event.target.closest('cart-remove-button');

    if (!relevant) return;

    clearTimeout(state.timer);

    state.timer = setTimeout(() => {
      scheduleNormalise();
    }, 900);
  }

  function scheduleNormalise() {
    clearTimeout(state.timer);

    state.timer = setTimeout(async () => {
      if (state.busy) return;
      state.busy = true;

      try {
        const cart = await getCart();
        const plan = await buildGlobalPlan(cart);

        if (plan.noChange) {
          updateTable();
          return;
        }

        const response = await applyPlan(plan, true);
        renderCartResponse(response);
        updateTable();
      } catch (error) {
        console.error('Tiered pricing normalise failed:', error);
      } finally {
        state.busy = false;
      }
    }, 250);
  }

  async function updateTable() {
    if (!state.pageProduct) return;

    try {
      const cart = await getCart();

      const cartQty = cart.items
        .filter(item => item.product_id === state.pageProduct.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      const effectiveQty = cartQty > 0 ? cartQty : getProductQuantity();

      getTierRows().forEach(row => row.classList.remove('is-active'));

      let activeRow = null;

      getTierRows().forEach(row => {
        const threshold = Number(row.dataset.tierThreshold);

        if (!Number.isNaN(threshold) && effectiveQty >= threshold) {
          activeRow = row;
        }
      });

      if (activeRow) activeRow.classList.add('is-active');
    } catch (error) {
      console.error('Tiered pricing table update failed:', error);
    }
  }

  function renderCartResponse(response) {
  if (window.location.pathname === '/cart') {
    window.location.reload();
    return;
  }

  const drawer = document.querySelector('cart-drawer');

  if (drawer && response?.sections && typeof drawer.renderContents === 'function') {
    drawer.classList.remove('is-empty');
    drawer.renderContents(response);
    return;
  }

  openDrawer();
}

  function openDrawer() {
    const drawer = document.querySelector('cart-drawer');

    if (!drawer) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    drawer.classList.add('active');
    drawer.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
  }
});